import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, apiErrorMessage, formatDate } from "@/lib/api";

const EMPTY = { title: "", description: "", youtube_url: "", thumbnail: "", guest: "", category_id: "", status: "published" };

export default function AdminPodcasts() {
  const [episodes, setEpisodes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = () => api.get("/podcasts/admin/all").then(({ data }) => setEpisodes(data));
  useEffect(() => {
    load();
    api.get("/categories").then(({ data }) => setCategories(data));
  }, []);

  const syncInvestivo = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/podcasts/admin/sync-investivo");
      if (data.added > 0) {
        toast.success(`Добавени ${data.added} нови епизода от @investivo1`);
      } else {
        toast.info("Няма нови епизоди от @investivo1 в момента");
      }
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSyncing(false);
    }
  };

  const fetchMeta = async () => {
    if (!form.youtube_url) return;
    setFetching(true);
    try {
      const { data } = await api.post("/podcasts/fetch-metadata", { youtube_url: form.youtube_url });
      setForm((f) => ({ ...f, title: f.title || data.title, thumbnail: data.thumbnail }));
      toast.success("Данните са извлечени от YouTube");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setFetching(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/podcasts/${editingId}`, form);
      } else {
        await api.post("/podcasts", form);
      }
      setForm(EMPTY);
      setEditingId(null);
      load();
      toast.success("Записано успешно");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const edit = (ep) => {
    setEditingId(ep.id);
    setForm({ title: ep.title, description: ep.description, youtube_url: ep.youtube_url, thumbnail: ep.thumbnail, guest: ep.guest, category_id: ep.category_id || "", status: ep.status });
  };

  const remove = async (id) => {
    if (!window.confirm("Изтриване на епизода?")) return;
    await api.delete(`/podcasts/${id}`);
    load();
  };

  return (
    <div data-testid="admin-podcasts-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-3xl font-bold text-[#1A1A1A]">Подкасти</h1>
        <Button onClick={syncInvestivo} disabled={syncing} variant="outline" data-testid="admin-podcast-sync-investivo-btn">
          {syncing ? "Синхронизиране..." : "Синхронизирай от @investivo1"}
        </Button>
      </div>
      <form onSubmit={submit} className="bg-white border border-[#E2E8F0] p-6 mb-10 space-y-4 max-w-2xl">
        <h2 className="font-semibold text-[#1A1A1A]">{editingId ? "Редактиране на епизод" : "Нов епизод"}</h2>
        <div className="flex gap-2">
          <Input placeholder="YouTube линк" value={form.youtube_url} onChange={(e) => setForm({ ...form, youtube_url: e.target.value })} data-testid="admin-podcast-youtube-url-input" />
          <Button type="button" onClick={fetchMeta} disabled={fetching} variant="outline" data-testid="admin-podcast-fetch-metadata-btn">
            {fetching ? "..." : "Извлечи данни"}
          </Button>
        </div>
        <Input placeholder="Заглавие" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="admin-podcast-title-input" />
        <Textarea placeholder="Описание" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="admin-podcast-description-input" />
        <div className="flex gap-3">
          <Input placeholder="Гост" value={form.guest} onChange={(e) => setForm({ ...form, guest: e.target.value })} data-testid="admin-podcast-guest-input" />
          <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
            <SelectTrigger data-testid="admin-podcast-category-select"><SelectValue placeholder="Категория" /></SelectTrigger>
            <SelectContent>
              {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {form.thumbnail && <img src={form.thumbnail} alt="" className="w-40 aspect-video object-cover" />}
        <div className="flex gap-3">
          <Button type="submit" className="bg-[#A6192E] hover:bg-[#8C1123]" data-testid="admin-podcast-submit-btn">{editingId ? "Запази" : "Публикувай"}</Button>
          {editingId && <Button type="button" variant="outline" onClick={() => { setEditingId(null); setForm(EMPTY); }}>Отказ</Button>}
        </div>
      </form>

      <div className="bg-white border border-[#E2E8F0] divide-y divide-[#E2E8F0]" data-testid="admin-podcasts-table">
        {episodes.map((ep) => (
          <div key={ep.id} className="flex items-center gap-4 p-4">
            <img src={ep.thumbnail} alt="" className="w-20 aspect-video object-cover" />
            <div className="flex-1">
              <div className="font-semibold text-[#1A1A1A]">{ep.title}</div>
              <div className="text-xs text-[#718096]">{formatDate(ep.published_at)} &middot; {ep.status}</div>
            </div>
            <Button size="sm" variant="outline" onClick={() => edit(ep)} data-testid={`admin-podcast-edit-${ep.id}`}>Редактирай</Button>
            <Button size="sm" variant="outline" onClick={() => remove(ep.id)} className="text-red-600 border-red-200" data-testid={`admin-podcast-delete-${ep.id}`}>Изтрий</Button>
          </div>
        ))}
      </div>
    </div>
  );
}
